/**
 * Methodology column scoping for deal custom fields.
 *
 * The backend seeds deal fields for EVERY sales methodology (BANT, MEDDIC,
 * MEDDPICC, SPICED) per workspace and materializes a grid-visible
 * crm_ingestion_field row for each, regardless of which summary template the
 * workspace picked (WorkspaceIntelligence.defaultSummaryTemplateId). These
 * helpers compute which field names to hide so grid surfaces only show the
 * picked methodology's columns.
 *
 * Attribution is template-driven, never name-driven: a built-in methodology
 * template's sections[].crmField values are Mongo DealField ids, which map to
 * bare field names (e.g. "metrics") via GetDealFields. Matching by label or
 * name vocabulary (see methodology-fields.ts) could hide a user-created field
 * that happens to share a name; template sections can only reference seeded
 * methodology fields, so this path has no false positives.
 *
 * Fields shared between methodologies (MEDDIC and MEDDPICC share metrics,
 * economic_buyer, decision_criteria, decision_process, pain, champion; SPICED
 * also uses pain) survive automatically: the picked template's own crmFields
 * are subtracted from the union before anything is excluded.
 */

export const BUILT_IN_METHODOLOGY_TEMPLATE_NAMES: ReadonlySet<string> = new Set([
  "BANT",
  "MEDDIC",
  "MEDDPICC",
  "SPICED",
]);

export type ScopeTemplate = {
  id: string;
  name?: string | null;
  isDefault?: boolean | null;
  isWorkspaceWide?: boolean | null;
  sections?: ReadonlyArray<{ crmField?: string | null } | null> | null;
};

/**
 * A backend-seeded methodology preset. `isDefault` distinguishes the seeded
 * rows from a user template that happens to be named "MEDDIC".
 */
export function isBuiltInMethodologyTemplate(template: ScopeTemplate): boolean {
  return (
    template.isDefault === true &&
    template.isWorkspaceWide === true &&
    BUILT_IN_METHODOLOGY_TEMPLATE_NAMES.has(template.name?.trim().toUpperCase() ?? "")
  );
}

/**
 * Resolve the workspace's picked summary template. `defaultSummaryTemplateId`
 * is normally a template id; the legacy literal sentinel "general" is treated
 * as unresolvable (null) so callers fail open. The sentinel is Settings'
 * never-touched default, i.e. "no one ever chose" — not "the user chose
 * General" — and SettingsPage resolves it to the FIRST isDefault template in
 * backend order (every seeded preset is isDefault), so mapping it to any
 * specific template here could both mass-hide columns for never-configured
 * workspaces and disagree with what Settings displays as the current pick.
 * An explicit General pick stores the General template's real id and filters
 * normally. Returns null when the pick is absent, the sentinel, or doesn't
 * match any known template.
 */
export function resolvePickedTemplate(
  templates: ReadonlyArray<ScopeTemplate>,
  defaultSummaryTemplateId: string | null | undefined,
): ScopeTemplate | null {
  if (!defaultSummaryTemplateId || defaultSummaryTemplateId === "general") return null;
  return templates.find(t => t.id === defaultSummaryTemplateId) ?? null;
}

function sectionFieldIds(template: ScopeTemplate): string[] {
  return (template.sections ?? [])
    .map(s => s?.crmField)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

/** Map a template's section crmField ids to resolvable bare field names. */
function sectionFieldNames(
  template: ScopeTemplate,
  dealFieldNameById: ReadonlyMap<string, string>,
): Set<string> {
  const names = new Set<string>();
  for (const id of sectionFieldIds(template)) {
    const name = dealFieldNameById.get(id);
    if (name) names.add(name);
  }
  return names;
}

/**
 * Bare field names (e.g. "metrics", "budget") to hide from deal grid surfaces.
 *
 * excluded = union(built-in methodology templates' field NAMES)
 *          − (picked template's field NAMES)
 * where names come from mapping `sections[].crmField` (Mongo DealField ids)
 * through `dealFieldNameById`. The set algebra is done on NAMES, not ids:
 * different templates can reference the same field name through different
 * DealField doc ids (fields soft-deleted and re-seeded, templates created in
 * different generations), and id-level subtraction would then hide the picked
 * methodology's own columns because another template re-contributes the same
 * name under a different id.
 *
 * Applied for ANY resolvable pick: picking "General" (which references no
 * crmFields) hides all methodology fields, and a custom template keeps exactly
 * the methodology fields its own sections reference.
 *
 * Fail-open rules — never exclude what can't be attributed:
 * - unresolvable pick → empty set;
 * - a crmField id missing from `dealFieldNameById` is skipped;
 * - a picked BUILT-IN METHODOLOGY template whose sections resolve to zero
 *   field names is a data anomaly (sections edited with mappings cleared to
 *   "none", or stale field ids after re-seeding) → empty set. Hiding would
 *   otherwise remove the picked methodology's own columns. General/custom
 *   picks legitimately resolve to zero names, so the guard is methodology-only.
 */
export function computeExcludedFieldNames(
  templates: ReadonlyArray<ScopeTemplate>,
  defaultSummaryTemplateId: string | null | undefined,
  dealFieldNameById: ReadonlyMap<string, string>,
): Set<string> {
  const picked = resolvePickedTemplate(templates, defaultSummaryTemplateId);
  if (!picked) return new Set();

  const pickedNames = sectionFieldNames(picked, dealFieldNameById);
  if (pickedNames.size === 0 && isBuiltInMethodologyTemplate(picked)) return new Set();

  const excluded = new Set<string>();
  for (const template of templates) {
    if (!isBuiltInMethodologyTemplate(template)) continue;
    for (const name of sectionFieldNames(template, dealFieldNameById)) {
      if (!pickedNames.has(name)) excluded.add(name);
    }
  }
  return excluded;
}
